import {useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import {Table} from 'src/feature/bite/component/Table'
import {useCopyPackage, useDeletePackage} from '../../context/useMutation'
import {usePackage} from '../../hook'

import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import {NumericFormat} from 'react-number-format'
import Swal from 'sweetalert2'
import {useIsMobile} from 'src/feature/bite/hook/useIsMobile'

const PackageList = ({loading, refetch}: {loading: boolean; refetch: () => void}) => {
  const intl = useIntl()
  const {list, listFilter, listFilterSet} = usePackage()
  const {mutateAsync: deletePackage} = useDeletePackage(refetch)
  const {mutateAsync: copyPackage} = useCopyPackage()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedPackageId, setSelectedPackageId] = useState<string>('')
  const open = Boolean(anchorEl)
  const isMobile = useIsMobile()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, packageId) => {
    setSelectedPackageId(packageId)
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setSelectedPackageId('')
    setAnchorEl(null)
  }

  const navigate = useNavigate()

  const handleRemovePackage = async (id: string) => {
    Swal.fire({
      title: ` Delete Confirmation`,
      text: 'Are you sure you want to delete this entry? This action cannot be undone.',
      iconHtml: '<i class="bi bi-x-circle-fill text-danger fs-4x"></i>',
      customClass: {
        icon: 'text-primary border-none border-0',
        title: 'fs-4',
        container: 'py-0 my-0',
        htmlContainer: 'pt-3 mt-3',
        confirmButton: 'me-10',
        actions: 'd-flex justify-content-between',
      },
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({id: 'YES'}),
      cancelButtonText: intl.formatMessage({id: 'NO'}),
      confirmButtonColor: '#5cb85c',
      cancelButtonColor: '#F1416C',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deletePackage(id)
      }
    })
  }

  const handleSendPaymentLink = (itemID: string) => {
    navigate(`/packages/${itemID}/send-payment-link`)
  }

  const columns = [
    {
      key: 'name',
      dataIndex: 'packageName',
      title: intl.formatMessage({id: 'MEMBER.DETAIL.PACKAGE.PACKAGE_NAME'}),
    },
    {
      key: 'Price',
      dataIndex: 'unitPrice',
      title: intl.formatMessage({id: 'PRODUCT.LIST.PRICE'}) + ' (AED)',
      render: (text: any) => (
        <NumericFormat
          value={text}
          displayType={'text'}
          thousandSeparator={true}
          fixedDecimalScale={true}
          decimalScale={2}
        />
      ),
    },
    {
      key: 'subscriptionOption',
      dataIndex: 'subscriptionOption',
      title: intl.formatMessage({id: 'MEMBER.DETAIL.PACKAGE.SUBSCRIPTION_OPTION'}),
      render: (text: any) => {
        /*  return <>{text === 1 ? 'Monthly' : 'Annually'}</> */
        switch (text) {
          case 1:
            return <>Monthly</>
          case 2:
            return <>Annually</>
          case 3:
            return <>One Time</>
          default:
            return <>-</>
        }
      },
    },
    {
      key: 'status',
      dataIndex: 'status',
      title: intl.formatMessage({id: 'PRODUCT.LIST.STATUS'}),
      render: (status: number) => {
        switch (status?.toString()) {
          case '1':
            return (
              <div>
                <span className={`badge badge-light-success`}>Active</span>
              </div>
            )
          case '2':
            return (
              <div>
                <span className={`badge badge-danger`}>Inactive</span>
              </div>
            )
          case '3':
            return (
              <div>
                <span className={`badge badge-light-warning`}>Preactive</span>
              </div>
            )
          default:
            return (
              <div>
                <span className={`badge badge-light`}>-</span>
              </div>
            )
        }
      },
    },
    {
      key: 'id',
      dataIndex: 'id',
      title: isMobile ? 'Payment Link' : '',
      render: (id: string) => {
        const _package = list?.dataList?.find((x) => x.id === id)
        return (
          <div className='min-w-0 md:min-w-250px d-flex justify-start md:justify-center'>
            <button
              className='btn btn-sm btn-dark d-flex align-items-center gap-3'
              disabled={_package?.status !== 1 || !_package?.paymentLinkEnable}
              onClick={() => {
                handleSendPaymentLink(id)
              }}
            >
              {intl.formatMessage({id: 'ITEM.SEND_PAYMENT_LINK'})}

              <i className='bi bi-send'></i>
            </button>
          </div>
        )
      },
    },
    {
      key: 'action',
      dataIndex: 'id',
      title: intl.formatMessage({id: 'PRODUCT.LIST.ACTION'}),
      render: (id: string) => (
        <div>
          <Button
            id='basic-button'
            aria-controls='basic-menu'
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
            onClick={(e) => handleClick(e, id)}
          >
            <i className='bi bi-three-dots-vertical fs-3'></i>
          </Button>
          <Menu
            elevation={0}
            id='basic-menu'
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            PaperProps={{
              style: {
                borderRadius: '6px',
                border: '1px solid #e1e7f3',
                padding: '10px',
              },
            }}
          >
            <a className='text-black' href={`/packages/edit/${selectedPackageId}`}>
              <MenuItem
                sx={{
                  '&:hover': {
                    backgroundColor: '#e1e7f3',
                    borderRadius: '6px',
                  },
                }}
              >
                Edit
              </MenuItem>
            </a>

            <MenuItem
              className='text-black'
              sx={{
                '&:hover': {
                  backgroundColor: '#e1e7f3',
                  borderRadius: '6px',
                },
              }}
              onClick={async () => {
                await copyPackage(selectedPackageId)
                handleClose()
              }}
            >
              Copy
            </MenuItem>
            {list?.dataList?.find((x) => x.id === selectedPackageId)?.isEditable && (
              <MenuItem
                className='text-black'
                sx={{
                  '&:hover': {
                    backgroundColor: '#e1e7f3',
                    borderRadius: '6px',
                  },
                }}
                onClick={async () => {
                  await handleRemovePackage(selectedPackageId)
                  handleClose()
                }}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
        </div>
      ),
    },
  ]

  return (
    <Table
      loading={loading}
      dataSource={list.dataList}
      columns={isMobile ? columns.filter((col) => col.key !== 'action') : columns}
      tableName={'products'}
      pagination={{
        current: listFilter.paging.currentPageNumber,
        pageSize: listFilter.paging.pageSize,
        total: list.paging.totalCount,
        onChange: (page: any) => {
          const {pageSize, current} = page
          const currentPage = pageSize !== listFilter.paging.pageSize ? 1 : current

          listFilterSet({
            ...listFilter,
            paging: {
              ...listFilter.paging,
              pageSize,
              currentPageNumber: currentPage,
            },
          })
        },
      }}
    />
  )
}

export default PackageList
